import React, { useMemo } from 'react';
import { Employee, Lead, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { kr, quoteTotal, totals } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { BarChart } from '../../components/pro/BarChart';
import { RankedBars } from '../../components/pro/RankedBars';
interface ProStatisticsProps {
  leads: Lead[];
  quotes: Quote[];
  jobs: ProJob[];
  invoices: ProInvoice[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  vatRate: number;
}
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export function ProStatistics({
  leads,
  quotes,
  jobs,
  invoices,
  employees,
  timeEntries,
  vatRate
}: ProStatisticsProps) {
  const invoiceTotalOf = (invoice: ProInvoice) => totals(invoice.lines, vatRate).total;
  const byMonth = useMemo(() => {
    const now = new Date();
    return Array.from({
      length: 6
    }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const inMonth = (iso: string) => {
        const value = new Date(iso);
        return value.getMonth() === date.getMonth() && value.getFullYear() === date.getFullYear();
      };
      return {
        label: MONTHS[date.getMonth()],
        revenue: invoices.filter(i => i.status !== 'kladde' && inMonth(i.invoiceDate)).reduce((sum, i) => sum + invoiceTotalOf(i), 0),
        leads: leads.filter(lead => inMonth(lead.createdDate)).length
      };
    });
  }, [invoices, leads, vatRate]);
  const kpis = useMemo(() => {
    const decided = leads.filter(lead => ['vundet', 'tabt'].includes(lead.stage));
    const won = decided.filter(lead => lead.stage === 'vundet');
    const answered = quotes.filter(quote => ['accepteret', 'afvist'].includes(quote.status));
    const accepted = answered.filter(quote => quote.status === 'accepteret');
    const revenue = invoices.filter(i => i.status !== 'kladde').reduce((sum, i) => sum + invoiceTotalOf(i), 0);
    const billable = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
    const allHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);
    return {
      conversion: decided.length ? Math.round(won.length / decided.length * 100) : 0,
      acceptance: answered.length ? Math.round(accepted.length / answered.length * 100) : 0,
      averageJob: invoices.length ? revenue / invoices.length : 0,
      billable,
      billableShare: allHours ? Math.round(billable / allHours * 100) : 0,
      revenue,
      newLeads: leads.length
    };
  }, [leads, quotes, invoices, timeEntries, vatRate]);

  /** Revenue by source, not lead count — ten cheap leads beat nothing. */
  const bySource = useMemo(() => {
    const map = new Map<string, {
      value: number;
      count: number;
    }>();
    leads.forEach(lead => {
      const current = map.get(lead.source) ?? {
        value: 0,
        count: 0
      };
      map.set(lead.source, {
        value: current.value + (lead.stage === 'vundet' ? lead.estimatedValue : 0),
        count: current.count + 1
      });
    });
    return Array.from(map.entries()).map(([label, data]) => ({
      label,
      value: data.value,
      meta: `${data.count} leads`
    }));
  }, [leads]);
  const sourceVolume = useMemo(() => {
    const map = new Map<string, number>();
    leads.forEach(lead => map.set(lead.source, (map.get(lead.source) ?? 0) + 1));
    return Array.from(map.entries()).map(([label, value]) => ({
      label,
      value
    }));
  }, [leads]);
  const byEmployee = useMemo(() => employees.map(employee => {
    const hours = timeEntries.filter(entry => entry.employeeId === employee.id && entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
    return {
      label: employee.name,
      value: hours * employee.hourlyRate,
      meta: `${hours.toLocaleString('da-DK')} t`
    };
  }), [employees, timeEntries]);
  const card = 'rounded-xl border border-stone-200 bg-white p-5';
  return <div className="space-y-5" data-id="element-1507" data-mp="18rbgae0xwkc2">
      <PageHeader title="Statistik" subtitle="Hvor kommer pengene fra, og hvad koster de at hente" data-id="element-1508" data-mp="14oonoawg2vdt" />

      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-stone-200 bg-stone-200 lg:grid-cols-6" data-id="element-1509" data-mp="21cuoemq8x3mt">
        {[{
        label: 'Omsætning i alt',
        value: kr(kpis.revenue)
      }, {
        label: 'Nye leads',
        value: kpis.newLeads
      }, {
        label: 'Konvertering',
        value: `${kpis.conversion} %`
      }, {
        label: 'Accepterede tilbud',
        value: `${kpis.acceptance} %`
      }, {
        label: 'Gns. opgavestørrelse',
        value: kr(kpis.averageJob)
      }, {
        label: 'Fakturerbare timer',
        value: `${kpis.billable.toLocaleString('da-DK')} t`
      }].map(kpi => <div key={kpi.label} className="bg-white p-4" data-id="element-1510" data-mp="1xnisnpype9m">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1511" data-mp="6v6w9leu5n0i">
              {kpi.label}
            </p>
            <p className="mt-1.5 text-xl font-bold tabular-nums text-stone-900" data-id="element-1512" data-mp="aybw1g7j7alg">
              {kpi.value}
            </p>
          </div>)}
      </section>

      {/* The headline question: which sources actually earn money */}
      <section className={card} data-id="element-1513" data-mp="psq44lpk4zlc">
        <h2 className="text-base font-semibold text-stone-900" data-id="element-1514" data-mp="prou8kka2j57">
          Omsætning pr. leadkilde
        </h2>
        <p className="mb-5 text-sm text-stone-500" data-id="element-1515" data-mp="ukrpqy8m82o1">
          Vundet værdi, ikke antal henvendelser — det er her
          markedsføringskronerne skal hen.
        </p>
        <RankedBars data={bySource} format={kr} data-id="element-1516" data-mp="13lpmcvgq57wl" />
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-id="element-1517" data-mp="18jhoeib32aei">
        <section className={card} data-id="element-1518" data-mp="18k0tgyfmx4b4">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-1519" data-mp="34mmf1vqdcmnh">
            Omsætning pr. måned
          </h2>
          <p className="mb-5 text-sm text-stone-500" data-id="element-1520" data-mp="2zob3ngkfe435">Seneste 6 måneder</p>
          <BarChart data={byMonth.map(m => ({
          label: m.label,
          value: m.revenue
        }))} format={kr} data-id="element-1521" data-mp="3ecp4xn271khl" />
        </section>

        <section className={card} data-id="element-1522" data-mp="39l73yz067jwm">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-1523" data-mp="39k5rqr7f4rdb">
            Nye leads pr. måned
          </h2>
          <p className="mb-5 text-sm text-stone-500" data-id="element-1524" data-mp="3jbs9z7i6bqpk">Seneste 6 måneder</p>
          <BarChart data={byMonth.map(m => ({
          label: m.label,
          value: m.leads
        }))} format={value => `${value} leads`} data-id="element-1525" data-mp="3jbigh9xx71f4" />
        </section>

        <section className={card} data-id="element-1526" data-mp="3sdrrs210iytk">
          <h2 className="mb-5 text-base font-semibold text-stone-900" data-id="element-1527" data-mp="3saniiq8oxcw1">
            Omsætning pr. medarbejder
          </h2>
          <RankedBars data={byEmployee} format={kr} data-id="element-1528" data-mp="1xyp84kynaayk" />
        </section>

        <section className={card} data-id="element-1529" data-mp="1y0s7vwplnigh">
          <h2 className="mb-5 text-base font-semibold text-stone-900" data-id="element-1530" data-mp="dfv8zh7286lh">
            Antal leads pr. kilde
          </h2>
          <RankedBars data={sourceVolume} format={value => `${value}`} data-id="element-1531" data-mp="iegarl1ymyc1" />
        </section>
      </div>
    </div>;
}